import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CalendarClock,
  Inbox,
  Lightbulb,
  Megaphone,
  TrendingUp,
  UsersRound,
  type LucideIcon,
} from 'lucide-react';
import { paths } from '../lib/paths';
import {
  type CompanyLead,
  type LeadStatus,
  LEAD_STATUSES,
  formatDate,
  intentLabel,
  leadStatusMeta,
} from '../lib/companyLeads';

export function CompanyDashboardPage() {
  const [leads, setLeads] = useState<CompanyLead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch('/api/contact/list', { credentials: 'same-origin' })
      .then(async (r) => {
        if (!r.ok) throw new Error(`${r.status}`);
        const data = (await r.json()) as { submissions: CompanyLead[] };
        setLeads(data.submissions);
      })
      .catch(() => setError('Could not load company pipeline.'))
      .finally(() => setLoading(false));
  }, []);

  const counts = useMemo(() => {
    const byStatus = Object.fromEntries(LEAD_STATUSES.map((status) => [status.value, 0])) as Record<LeadStatus, number>;
    for (const lead of leads) byStatus[lead.status ?? 'new'] += 1;
    return byStatus;
  }, [leads]);

  const openPipeline = counts.new + counts.contacted + counts.demo_scheduled + counts.proposal_sent + counts.nurture;
  const unread = leads.filter((lead) => !lead.read).length;
  const followUpsDue = leads.filter((lead) => {
    if (!lead.follow_up_at || lead.status === 'won' || lead.status === 'lost') return false;
    return new Date(lead.follow_up_at).getTime() <= Date.now();
  }).length;
  const recent = leads.slice(0, 5);

  return (
    <div className="stack" style={{ gap: 18 }}>
      <section className="card" style={{ padding: 24 }}>
        <div className="eyebrow">Scorekeeper HQ</div>
        <h1 style={{ margin: '6px 0 0', fontSize: 30 }}>Company dashboard</h1>
        <p className="muted" style={{ maxWidth: 760, marginTop: 10 }}>
          Run the business side of The Scorekeeper: sales pipeline, customer
          movement, growth work, and product feedback. League operations stay
          in League HQ.
        </p>
        <div className="row" style={{ gap: 10, marginTop: 18 }}>
          <Link to={paths.companyPipeline} className="btn btn--primary">
            Open pipeline
          </Link>
          <Link to={paths.adminHome} className="btn">
            Switch to League HQ
          </Link>
        </div>
      </section>

      <div className="grid grid--4" style={{ gap: 16 }}>
        <MetricCard icon={Inbox} label="Open pipeline" value={loading ? '...' : String(openPipeline)} hint={`${unread} unread`} />
        <MetricCard icon={CalendarClock} label="Follow-ups due" value={loading ? '...' : String(followUpsDue)} hint="Active leads only" />
        <MetricCard icon={UsersRound} label="Customers" value={loading ? '...' : String(counts.won)} hint="Won leads in v1" />
        <MetricCard icon={TrendingUp} label="Won / lost" value={loading ? '...' : `${counts.won}/${counts.lost}`} hint="Pipeline outcome" />
      </div>

      <div className="grid grid--2" style={{ gap: 16 }}>
        <section className="card" style={{ padding: 22 }}>
          <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
            <h3 className="card__title" style={{ margin: 0 }}>
              <Inbox size={16} style={{ verticalAlign: -2, marginRight: 6 }} />
              Recent pipeline
            </h3>
            <Link to={paths.companyPipeline} className="btn btn--ghost btn--sm">
              Review
            </Link>
          </div>

          {loading ? (
            <p className="muted">Loading leads...</p>
          ) : error ? (
            <p style={{ color: 'var(--color-danger, #e53e3e)' }}>{error}</p>
          ) : recent.length === 0 ? (
            <p className="muted">No leads yet.</p>
          ) : (
            <div className="stack" style={{ gap: 10, marginTop: 14 }}>
              {recent.map((lead) => {
                const status = leadStatusMeta(lead.status);
                return (
                  <div
                    key={lead.id}
                    className="card"
                    style={{ padding: 14, background: 'var(--surface-2, var(--surface))' }}
                  >
                    <div className="row" style={{ justifyContent: 'space-between', gap: 10 }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 700 }}>{lead.organization_name || lead.name}</div>
                        <div className="muted" style={{ fontSize: 13 }}>{lead.name} - {intentLabel(lead.intent)}</div>
                      </div>
                      <span className={`chip chip--${status.tone}`}>{status.label}</span>
                    </div>
                    <div className="muted" style={{ fontSize: 12, marginTop: 8 }}>
                      Follow-up: {formatDate(lead.follow_up_at)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        <div className="stack" style={{ gap: 16 }}>
          <CompanyNextCard
            icon={UsersRound}
            title="Customers"
            body="Won leads become v1 customers, with pricing terms kept on the company side."
            to={paths.companyCustomers}
          />
          <CompanyNextCard
            icon={Megaphone}
            title="Marketing/SEO"
            body="Track public pages, CTA checks, content ideas, and SEO tasks without adding a CMS yet."
            to={paths.companyMarketing}
          />
          <CompanyNextCard
            icon={Lightbulb}
            title="Product feedback"
            body="Keep partner wishlist items and league feedback separate from league operations."
            to={paths.companyFeedback}
          />
        </div>
      </div>
    </div>
  );
}

function MetricCard({
  icon: Icon,
  label,
  value,
  hint,
}: {
  icon: LucideIcon;
  label: string;
  value: string;
  hint: string;
}) {
  return (
    <div className="card" style={{ padding: 18 }}>
      <div className="row" style={{ gap: 8, alignItems: 'center' }}>
        <Icon size={16} />
        <div className="stat__label">{label}</div>
      </div>
      <div className="stat__value" style={{ marginTop: 10 }}>{value}</div>
      <div className="stat__hint">{hint}</div>
    </div>
  );
}

function CompanyNextCard({
  icon: Icon,
  title,
  body,
  to,
}: {
  icon: LucideIcon;
  title: string;
  body: string;
  to: string;
}) {
  return (
    <div className="card" style={{ padding: 22 }}>
      <h3 className="card__title" style={{ margin: 0 }}>
        <Icon size={16} style={{ verticalAlign: -2, marginRight: 6 }} />
        {title}
      </h3>
      <p className="muted" style={{ marginTop: 8 }}>{body}</p>
      <Link to={to} className="btn btn--ghost btn--sm">
        Open
      </Link>
    </div>
  );
}
